import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import { TooltipInfoIcon } from 'icone';
import React, { MouseEvent, MutableRefObject, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';

interface LegendProps {
	gridRef: MutableRefObject<AgGridReact>;
}

const Legend: React.FC<LegendProps> = ({ gridRef }) => {
	const legendElements = [
		{
			name: 'Work Packages',
			field: 'wp',
			backgroundColor: 'var(--wx-gantt-summary-color)',
			shape: 'square',
			description: 'Blocco operativo principale: raggruppa attività omogenee sotto un obiettivo condiviso.',
			example: 'Es. "Progettazione impianto" (WP) → include Task come "Acquisto materiali", "Collaudo".',
		},
		{
			name: 'Milestones',
			field: 'milestone',
			backgroundColor: 'var(--wx-gantt-milestone-color)',
			shape: 'triangle',
			description:
				'Evento decisionale: punto di non ritorno che abilita le fasi successive. Un checkpoint contrattuale definito negli accordi con clienti/partner esterni.',
			example: 'Es: “Milestone: Approvazione normativa" → Rischio associato: Alto.',
		},
		{
			name: 'Deliverables',
			field: 'deliverable',
			backgroundColor: 'var(--wx-gantt-milestone-color)',
			shape: 'diamond',
			description:
				'Risultato verificabile: consegnabile solo se rispetta criteri prestabiliti. Un output contrattuale come un documento o un oggetto fisico richiesto da accordi formali.',
			example: 'Es: "Deliverable: Manuale utente finale" → Formato: PDF e cartaceo.',
		},
		{
			name: 'Task',
			field: 'task',
			backgroundColor: 'var(--wx-gantt-task-color)',
			shape: 'square',
			description:
				'Step operativo: compito assegnato a un reparto/ruolo, con deliverable chiaro. Attività cardine: azione necessaria per avanzare nella macrofase (WP).',
			example: 'Es. Task: "Approvazione budget" → Owner: CFO | Input: Report finanziario.',
		},
		{
			name: 'Subtask',
			field: 'subtask',
			backgroundColor: 'var(--wx-gantt-milestone-color)',
			shape: 'circle',
			description:
				'Checkpoint interno: step validato per garantire qualità prima di procedere. Può essere considerata una micro-azione che viene assegnata a un singolo executor, senza ulteriore scomposizione.',
			example: 'Es. Subtask: "Verifica preliminare da parte del QA".',
		},
	] as const;

	const [isModalOpen, setIsModalOpen] = useState(false);

	const handleOnClick = (event: MouseEvent<HTMLButtonElement>, legendElement: (typeof legendElements)[number]) => {
		const currentTarget = event.currentTarget instanceof HTMLButtonElement && event.currentTarget;

		if (currentTarget.classList.contains('hiding')) {
			currentTarget.classList.remove('hiding');
		} else {
			currentTarget.classList.add('hiding');
		}

		const selectedGanttElements = document.querySelectorAll(`.wx-bar[data-id^='${legendElement.field}']`) as NodeListOf<HTMLElement>;
		if (selectedGanttElements.length > 0) {
			selectedGanttElements.forEach((selectedGanttElement) => {
				if (selectedGanttElement.style.opacity === '0') {
					selectedGanttElement.style.opacity = '1';
					selectedGanttElement.style.pointerEvents = 'auto';

					const selectedTaskDetailsPopover = document.querySelector(
						`#detail_popover-${selectedGanttElement.getAttribute('data-id')}`,
					) as HTMLElement | null;
					if (!selectedTaskDetailsPopover) return;
					selectedTaskDetailsPopover.style.display = 'block';
					return;
				}
				selectedGanttElement.style.opacity = '0';
				selectedGanttElement.style.pointerEvents = 'none';
				const selectedTaskDetailsPopover = document.querySelector(
					`#detail_popover-${selectedGanttElement.getAttribute('data-id')}`,
				) as HTMLElement | null;
				if (!selectedTaskDetailsPopover) return;
				selectedTaskDetailsPopover.style.display = 'none';
			});
		}

		const selectedRows = gridRef.current.api.getSelectedRows();
		if (selectedRows.length === 0 || !selectedRows[0].id.startsWith(legendElement.field)) return;
		const renderedNodes = gridRef.current.api.getRenderedNodes();

		const selectedNode = renderedNodes.find((node) => node.data.id === selectedRows[0].id);

		if (!selectedNode) return;
		gridRef.current.api.deselectIndex(selectedNode.rowIndex);
	};

	const renderLegendElement = (legendElement: (typeof legendElements)[number]) => {
		const size = '27px';
		switch (legendElement.shape) {
			case 'square':
				return (
					<div
						style={{
							width: size,
							height: size,
							backgroundColor: legendElement.backgroundColor,
							borderRadius: 'var(--wx-gantt-bar-border-radius)',
						}}
					/>
				);
			case 'triangle':
				return (
					<div
						className="milestone-element"
						style={{
							width: size,
							height: size,
							backgroundColor: legendElement.backgroundColor,
							borderRadius: 'var(--wx-gantt-bar-border-radius)',
						}}
					/>
				);
			case 'circle':
				return (
					<div
						style={{
							width: size,
							height: size,
							backgroundColor: legendElement.backgroundColor,
							borderRadius: '100%',
						}}
					/>
				);
			case 'diamond':
				return (
					<div
						style={{
							width: size,
							height: size,
							backgroundColor: legendElement.backgroundColor,
							// borderRadius: 'var(--wx-gantt-milestone-border-radius)',
							borderRadius: 'var(--wx-gantt-bar-border-radius)',
							rotate: '45deg',
							scale: '0.75',
						}}
					/>
				);
		}
	};
	return (
		<div>
			<div style={{ fontSize: '16px', lineHeight: '25px', fontWeight: '500' }}>Legenda:</div>
			<div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
				<ul
					style={{
						display: 'flex',
						gap: '1rem',
						listStyle: 'none',
						margin: '0',
						marginTop: '12px',
						padding: '0',
						flexWrap: 'wrap',
					}}
				>
					{legendElements.map((legendElement) => (
						<li key={legendElement.name}>
							<Button
								id={`wx-legend-button-${legendElement.field}`}
								className="wx-legend-button"
								variant="outline"
								onClick={(event) => {
									handleOnClick(event, legendElement);
								}}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '0.5rem',
									fontSize: '16px',
									lineHeight: '25px',
									padding: '8px 16px',
									border: '3px solid var(--np-primary-color)',
									borderRadius: '8px',
									fontWeight: 400,
									textTransform: 'none',
								}}
							>
								{renderLegendElement(legendElement)}
								{/* {(() => {
									const size = '27px';
									switch (legendElement.shape) {
										case 'square':
											return (
												<div
													style={{
														width: size,
														height: size,
														backgroundColor: legendElement.backgroundColor,
														borderRadius: 'var(--wx-gantt-bar-border-radius)',
													}}
												/>
											);
										case 'triangle':
											return (
												<div
													style={{
														width: '0',
														height: '0',
														borderLeft: `${parseInt(size) / 1.5 + 'px'} solid transparent`,
														borderRight: `${parseInt(size) / 1.5 + 'px'} solid transparent`,
														borderBottom: `${size} solid ${legendElement.backgroundColor}`,
													}}
												/>
											);
										case 'circle':
											return (
												<div
													style={{
														width: size,
														height: size,
														backgroundColor: legendElement.backgroundColor,
														borderRadius: '100%',
													}}
												/>
											);
										case 'diamond':
											return (
												<div
													style={{
														width: size,
														height: size,
														backgroundColor: legendElement.backgroundColor,
														borderRadius: 'var(--wx-gantt-milestone-border-radius)',
														rotate: '45deg',
														scale: '0.75',
													}}
												/>
											);
									}
								})()} */}
								<span>{legendElement.name}</span>
							</Button>
						</li>
					))}
				</ul>
				<Button
					onClick={() => {
						setIsModalOpen(true);
					}}
					variant="outline"
					style={{ color: 'var(--np-primary-color)', marginTop: '12px', minWidth: '50px' }}
				>
					<TooltipInfoIcon width={24} height={24} />
				</Button>
			</div>
			<Modal size="xl" show={isModalOpen} onHide={() => setIsModalOpen(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title style={{ fontSize: '32px', fontWeight: 700, marginTop: '30px' }}>Approfondimento</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<ul
						style={{
							listStyle: 'none',
							margin: '0',
							padding: '0',
						}}
					>
						{legendElements.map((legendElement, index) => (
							<li
								key={legendElement.name}
								style={{
									display: 'grid',
									gridTemplateColumns: '27px auto',
									gap: '18px',
									fontSize: '16px',
									lineHeight: '25px',
									padding: '8px 16px',
									borderBottom: index === legendElements.length - 1 ? 'none' : '1px solid #E9ECEF',
									marginTop: index === 0 ? '0' : '24px',
								}}
							>
								<div style={{ marginTop: '6px' }}>
									{renderLegendElement(legendElement)}
									{/* {(() => {
										const size = '27px';
										switch (legendElement.shape) {
											case 'square':
												return (
													<div
														style={{
															width: size,
															height: size,
															backgroundColor: legendElement.backgroundColor,
															borderRadius: 'var(--wx-gantt-bar-border-radius)',
														}}
													/>
												);
											case 'triangle':
												return (
													<div
														style={{
															width: '0',
															height: '0',
															borderLeft: `${parseInt(size) / 2 + 'px'} solid transparent`,
															borderRight: `${parseInt(size) / 2 + 'px'} solid transparent`,
															borderBottom: `${size} solid ${legendElement.backgroundColor}`,
														}}
													/>
												);
											case 'circle':
												return (
													<div
														style={{
															width: size,
															height: size,
															backgroundColor: legendElement.backgroundColor,
															borderRadius: '100%',
														}}
													/>
												);
											case 'diamond':
												return (
													<div
														style={{
															width: size,
															height: size,
															backgroundColor: legendElement.backgroundColor,
															borderRadius: 'var(--wx-gantt-milestone-border-radius)',
															rotate: '45deg',
															scale: '0.75',
														}}
													/>
												);
										}
									})()} */}
								</div>
								<div style={{ fontSize: '24px' }}>
									<div style={{ fontWeight: 700, marginBottom: '8px' }}>{legendElement.name}</div>
									<div style={{ paddingRight: '2.5rem', marginBottom: '12px' }}>{legendElement.description}</div>
									<div style={{ fontStyle: 'italic', marginBottom: '24px', fontSize: '18px' }}>{legendElement.example}</div>
								</div>
							</li>
						))}
					</ul>
				</Modal.Body>
			</Modal>
		</div>
	);
};

export default Legend;
